import React, { useMemo } from "react";
import { Popover } from "@mui/material";
import { useTranslation } from "react-i18next";
import { formatAmount, formatDollarAmount, buildHeadCells } from "../../../../utils/methods";
import { Edit as EditInterface } from "../../../../interfaces/reservation.interface";
import { TableContextProvider } from "../../../../interfaces/common";
import { SortOrder } from "../../../../enums";
import { VALIDATION_FIELD_MAPPING } from "../../../../utils/appConstant";
import CalculationPopoverRow from "./CalculationPopoverRow";
import TableBox from "../../../../components/TableBox";

interface CalculationPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  formData: EditInterface;
  type: 'accommodation' | 'guestFees';
}

const CalculationPopover: React.FC<CalculationPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  formData,
  type,
}) => {
  const { t } = useTranslation();
  const platformCommission = formData.platformCommission;
  const hasPlatformCommission = formData.platformCommission !== null && formData.platformCommission !== undefined && Number(formData.platformCommission) !== 0;

  const formatNumber = (num: number | string): string => {
    return (Number(num) || 0).toFixed(2);
  };

  const calculateNet = (value: string) => {
    const mainValue = Number(value) || 0;
    if (!platformCommission) return mainValue.toFixed(2);
    const commissionAmount = mainValue * (Number(platformCommission) / 100);
    return (mainValue - commissionAmount).toFixed(2);
  };

  const accommodationRows = [
    {
      name: t('reservations.edit.pageTexts.form.xNightRoomFees.label'),
      amount: formatDollarAmount(formatAmount(formData.xNightRoomFees || '0')),
      platformCommission: platformCommission ? `${formatNumber(platformCommission)}%` : '-',
      net: formatDollarAmount(formatAmount(calculateNet(formData.xNightRoomFees || '0'))),
    },
  ];

  const guestFeesRows = [
    {
      name: t('reservations.edit.pageTexts.form.cleaningFees.label'),
      amount: formatDollarAmount(formatAmount(formData.cleaningFees || '0')),
      platformCommission: platformCommission ? `${formatNumber(platformCommission)}%` : '-',
      net: formatDollarAmount(formatAmount(calculateNet(formData.cleaningFees || '0'))),
    },
    {
      name: t('reservations.edit.pageTexts.form.linenFees.label'),
      amount: formatDollarAmount(formatAmount(formData.linenFees || '0')),
      platformCommission: platformCommission ? `${formatNumber(platformCommission)}%` : '-',
      net: formatDollarAmount(formatAmount(calculateNet(formData.linenFees || '0'))),
    },
    {
      name: t('reservations.edit.pageTexts.form.petFees.label'),
      amount: formatDollarAmount(formatAmount(formData.petFees || '0')),
      platformCommission: platformCommission ? `${formatNumber(platformCommission)}%` : '-',
      net: formatDollarAmount(formatAmount(calculateNet(formData.petFees || '0'))),
    },
  ];

  const rows = type === 'accommodation' ? accommodationRows : guestFeesRows;

  const headCells = buildHeadCells([
    VALIDATION_FIELD_MAPPING.name,
    VALIDATION_FIELD_MAPPING.amount,
    VALIDATION_FIELD_MAPPING.platformCommission,
    VALIDATION_FIELD_MAPPING.net,
  ], 'calculationTable');

  const tableValues: TableContextProvider = useMemo(() => {
    return {
      headCells,
      row: CalculationPopoverRow,
      data: rows,
      order: SortOrder.asc,
      orderBy: VALIDATION_FIELD_MAPPING.name,
      page: 1,
      perPage: 10,
      total: rows.length,
      loading: false,
      pagination: false,
      noDataFoundMessage: t('reservations.edit.pageTexts.form.calculationTable.noPlatformCommission'),
      setPage: () => {},
      setPerPage: () => {},
      setOrder: () => {},
      setOrderBy: () => {},
    };
  }, [headCells, rows, t]);

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <div style={{ minWidth: '400px'}}>
        {hasPlatformCommission ? (
          <TableBox tableValues={tableValues} />
        ) : (
          <div className="card mb-0">
            <div className="card-body text-center py-4">
              <p className="mb-0 text-muted">{t('reservations.edit.pageTexts.form.calculationTable.noPlatformCommission')}</p>
            </div>
          </div>
        )}
      </div>
    </Popover>
  );
};

export default CalculationPopover;

